import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Spacer,
  TagPicker,
  TagOption,
  Field,
  useTheme,
} from '@tw/tw-component-library';
import { Button, Heading } from '@tw/tw-component-library/common-wt';
import { useFormContext } from '../../../contexts/useFormContext';
import { useTagPickerOperations } from '../../../hooks/useTagPickerOperations';
import { reassignRequest } from '../../../apis/workflow/post/reassignRequest';
import { UseAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { createLogger } from '../../../utils/logger';

interface ReassignModalProps {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  onClose: () => void;
}

type AssigneeFieldKey =
  | 'responsible_lawyer'
  | 'first_partner'
  | 'second_partner'
  | 'second_partner_above_25k'
  | 'second_partner_below_25k';

type ReassignmentSectionKey =
  | 'resp_lawyer'
  | 'first_partner'
  | 'second_partner'
  | 'second_partner_above_25k'
  | 'second_partner_below_25k';

type AssigneeEmailValues = Record<AssigneeFieldKey, string>;

const EMPTY_ASSIGNEE_EMAILS: AssigneeEmailValues = {
  responsible_lawyer: '',
  first_partner: '',
  second_partner: '',
  second_partner_above_25k: '',
  second_partner_below_25k: '',
};

const FIELD_TO_SECTION: Record<AssigneeFieldKey, ReassignmentSectionKey> = {
  responsible_lawyer: 'resp_lawyer',
  first_partner: 'first_partner',
  second_partner: 'second_partner',
  second_partner_above_25k: 'second_partner_above_25k',
  second_partner_below_25k: 'second_partner_below_25k',
};

const SECTION_NAMES: Record<ReassignmentSectionKey, string> = {
  resp_lawyer: 'Responsible Lawyer',
  first_partner: 'First Partner',
  second_partner: 'Second Partner',
  second_partner_above_25k: 'Second Partner (Above 25k)',
  second_partner_below_25k: 'Second Partner (Below 25k)',
};

/** Normalize tag picker / form assignee values into a comparable identity string. */
const getAssigneeIdentity = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return '';
    return getAssigneeIdentity(value[0]);
  }

  if (typeof value === 'object') {
    const record = value as { value?: unknown; email?: unknown };
    if (typeof record.email === 'string' && record.email.trim()) {
      return record.email.trim();
    }
    if (typeof record.value === 'string' && record.value.trim()) {
      return record.value.trim();
    }
  }

  return '';
};

/** Value to send in the API payload (email, or '' for remove). */
const toReassignmentEmail = (identity: string): string => {
  return identity.includes('@') ? identity : identity ? identity : '';
};

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const ReassignModal: React.FC<ReassignModalProps> = ({
  isOpen,
  setIsOpen,
  onClose,
}) => {
  const { theme } = useTheme();
  const { formState, updateFormData } = useFormContext();
  const { processTagPickerField, tagPickerState } = useTagPickerOperations();
  const { getAccessToken, spoofedUser } = UseAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const navigate = useNavigate();

  const [initialEmails, setInitialEmails] =
    useState<AssigneeEmailValues>(EMPTY_ASSIGNEE_EMAILS);
  // Changed fields only: email for reassignment, '' for removal
  const [pendingChanges, setPendingChanges] = useState<
    Partial<Record<AssigneeFieldKey, string>>
  >({});

  // Create logger instance for ReassignModal
  const logger = createLogger('ReassignModal');

  logger.debug('Tag picker state', { tagPickerState });

  // Snapshot emails when modal opens; reset pending changes
  useEffect(() => {
    if (!isOpen) return;

    setInitialEmails({
      responsible_lawyer: getAssigneeIdentity(
        formState.data?.responsible_lawyer
      ),
      first_partner: getAssigneeIdentity(formState.data?.first_partner),
      second_partner: getAssigneeIdentity(formState.data?.second_partner),
      second_partner_above_25k: getAssigneeIdentity(
        formState.data?.second_partner_above_25k
      ),
      second_partner_below_25k: getAssigneeIdentity(
        formState.data?.second_partner_below_25k
      ),
    });
    setPendingChanges({});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- snapshot once on open
  }, [isOpen]);

  const respLawyerDisabled = useMemo(() => {
    const currentSection = formState.currentSection;
    // Can only edit resp_lawyer if current section is resp_lawyer
    return currentSection !== 'resp_lawyer';
  }, [formState.currentSection]);

  const firstPartnerDisabled = useMemo(() => {
    const currentSection = formState.currentSection;
    // Can edit first_partner if current section is resp_lawyer or first_partner
    return (
      currentSection !== 'resp_lawyer' && currentSection !== 'first_partner'
    );
  }, [formState.currentSection]);

  const secondPartnerDisabled = useMemo(() => {
    const currentSection = formState.currentSection;
    // Can edit second_partner if current section is resp_lawyer, first_partner, or any second_partner variation
    return (
      currentSection !== 'resp_lawyer' &&
      currentSection !== 'first_partner' &&
      currentSection !== 'second_partner' &&
      currentSection !== 'second_partner_above_25k' &&
      currentSection !== 'second_partner_below_25k'
    );
  }, [formState.currentSection]);

  const makeField = (name: AssigneeFieldKey, label: string): Field =>
    ({
      type: 'tagPicker',
      name,
      label,
      extras: {
        tagPicker: {
          maxTags: 1,
        },
      },
    } as unknown as Field);

  const respField = makeField('responsible_lawyer', 'Responsible Lawyer');
  const firstField = makeField('first_partner', 'First Partner');
  const secondField = makeField('second_partner', 'Second Partner');

  const processedResp = processTagPickerField(respField) as unknown as {
    extras: {
      tagPicker: {
        suggestions?: TagOption[];
        preTags?: TagOption[];
        maxTags?: number;
        onSearch?: (search: string) => void;
        isLoading?: boolean;
        minSearchLength?: number;
      };
    };
  };
  const processedFirst = processTagPickerField(firstField) as unknown as {
    extras: {
      tagPicker: {
        suggestions?: TagOption[];
        preTags?: TagOption[];
        maxTags?: number;
        onSearch?: (search: string) => void;
        isLoading?: boolean;
        minSearchLength?: number;
      };
    };
  };
  const processedSecond = processTagPickerField(secondField) as unknown as {
    extras: {
      tagPicker: {
        suggestions?: TagOption[];
        preTags?: TagOption[];
        maxTags?: number;
        onSearch?: (search: string) => void;
        isLoading?: boolean;
        minSearchLength?: number;
      };
    };
  };

  const respProps = processedResp.extras.tagPicker;
  const firstProps = processedFirst.extras.tagPicker;
  const secondProps = processedSecond.extras.tagPicker;

  const handleChange =
    (key: AssigneeFieldKey) => (tags: TagOption[]) => {
      updateFormData({ [key]: tags });

      // Empty tags => removal (send ''). New/different assignee => reassignment.
      const nextIdentity = getAssigneeIdentity(tags);

      setPendingChanges((prev) => {
        const next = { ...prev };
        if (nextIdentity !== initialEmails[key]) {
          // Store the payload value immediately ('' for remove)
          next[key] = toReassignmentEmail(nextIdentity);
        } else {
          delete next[key];
        }
        return next;
      });
    };

  const handleSubmit = async () => {
    setIsLoading(true);
    const workflowId = formState.data?.id as number | undefined;
    if (!workflowId) {
      onClose();
      setIsOpen(false);
      setIsLoading(false);
      return;
    }

    // Only send fields the user changed (including removals as '').
    const sectionReassignments: Partial<
      Record<ReassignmentSectionKey, string>
    > = {};

    (Object.keys(pendingChanges) as AssigneeFieldKey[]).forEach((fieldKey) => {
      sectionReassignments[FIELD_TO_SECTION[fieldKey]] =
        pendingChanges[fieldKey] ?? '';
    });

    if (Object.keys(sectionReassignments).length === 0) {
      logger.debug('No assignee changes to submit');
      setIsLoading(false);
      return;
    }

    const submittedData = {
      sectionReassignments,
    };

    try {
      const response = await reassignRequest({
        id: workflowId,
        submittedData,
        getAccessToken,
        spoofedUser: spoofedUser?.value || null,
      });

      if (!response.success) {
        logger.error('Reassign request failed', {
          status: response.status,
          error: response.error,
          errors: response.errors,
        });
        return;
      }

      logger.debug('Reassign response', { response });

      const reassignedSections = Object.entries(sectionReassignments)
        .map(([section, value]) => {
          const label = SECTION_NAMES[section as ReassignmentSectionKey];
          return value ? `${label}: ${value}` : `${label}: removed`;
        })
        .join(', ');

      setSuccessMessage(
        `Reassignment successful! Changes: ${reassignedSections}`
      );
      setIsSuccess(true);

      setTimeout(() => {
        setIsSuccess(false);
        setSuccessMessage('');
        onClose();
        setIsOpen(false);
        navigate('/');
      }, 2000);
    } finally {
      setIsLoading(false);
    }
  };

  // Conditional content based on success state
  const modalContent = isSuccess ? (
    <div style={{ textAlign: 'center', padding: '2rem' }}>
      <div
        style={{
          fontSize: '1.2rem',
          color: theme.colors.success,
          marginBottom: '1rem',
          fontWeight: 'bold',
        }}
      >
        ✅ Success!
      </div>
      <div
        style={{
          fontSize: '1rem',
          color: theme.colors.text.primary,
          lineHeight: '1.5',
        }}
      >
        {successMessage}
      </div>
      <div
        style={{
          fontSize: '0.9rem',
          color: theme.colors.text.secondary,
          marginTop: '1rem',
        }}
      >
        Redirecting to dashboard...
      </div>
    </div>
  ) : (
    <>
      <Heading level={3}>Update assignees</Heading>
      <Spacer />
      <Wrapper>
        <TagPicker
          label="Responsible Lawyer"
          disabled={respLawyerDisabled}
          suggestions={
            (tagPickerState.suggestions?.responsible_lawyer as TagOption[]) ||
            []
          }
          preTags={respProps.preTags}
          maxTags={respProps.maxTags}
          onSearch={respProps.onSearch}
          isLoading={
            (
              (tagPickerState.suggestions?.responsible_lawyer as TagOption[]) ||
              []
            ).length > 0
              ? false
              : Boolean(tagPickerState.loadingFields?.responsible_lawyer)
          }
          minSearchLength={respProps.minSearchLength}
          onTagsChange={handleChange('responsible_lawyer')}
        />
        <TagPicker
          label="First Partner"
          disabled={firstPartnerDisabled}
          suggestions={
            (tagPickerState.suggestions?.first_partner as TagOption[]) || []
          }
          preTags={firstProps.preTags}
          maxTags={firstProps.maxTags}
          onSearch={firstProps.onSearch}
          isLoading={
            ((tagPickerState.suggestions?.first_partner as TagOption[]) || [])
              .length > 0
              ? false
              : Boolean(tagPickerState.loadingFields?.first_partner)
          }
          minSearchLength={firstProps.minSearchLength}
          onTagsChange={handleChange('first_partner')}
        />
        <TagPicker
          label="Second Partner"
          disabled={secondPartnerDisabled}
          suggestions={
            (tagPickerState.suggestions?.second_partner as TagOption[]) || []
          }
          preTags={secondProps.preTags}
          maxTags={secondProps.maxTags}
          onSearch={secondProps.onSearch}
          isLoading={
            ((tagPickerState.suggestions?.second_partner as TagOption[]) || [])
              .length > 0
              ? false
              : Boolean(tagPickerState.loadingFields?.second_partner)
          }
          minSearchLength={secondProps.minSearchLength}
          onTagsChange={handleChange('second_partner')}
        />
      </Wrapper>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
        setIsLoading(false);
        setIsSuccess(false);
        setIsOpen(false);
      }}
      footer={
        !isSuccess ? (
          <Button size="sm" onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? 'Updating...' : 'Update'}
          </Button>
        ) : null
      }
      title={isSuccess ? 'Success' : 'Reassign'}
      size="large"
      focusFirstInput={!isSuccess}
    >
      {modalContent}
    </Modal>
  );
};
